import { z } from 'zod';

export const vitalsSchema = z.object({
  temperature: z.number().min(35).max(42).optional(),
  heartRate: z.number().min(40).max(250).optional(),
  respiratoryRate: z.number().min(10).max(80).optional(),
  crt: z.number().min(0).max(5).optional(),
  painScore: z.number().min(0).max(10).optional(),
  bcs: z.number().min(1).max(9).optional(),
  mucosa: z.string().optional(),
  weight: z.number().min(0).max(200).optional(),
  recordedAt: z.string().optional()
});

export const anamnesisSchema = z.object({
  nutrition: z.string().optional(),
  deworming: z.string().optional(),
  dewormingLast: z.string().optional(),
  currentMeds: z.string().optional(),
  allergies: z.string().optional(),
  environment: z.string().optional(),
  reproduction: z.string().optional()
});

export const examSchema = z.object({
  allNormal: z.boolean().optional(),
  notes: z.string().optional(),
  systems: z.record(z.string(), z.enum(['normal', 'abnormal', 'not_examined'])).optional(),
  systemNotes: z.record(z.string(), z.string()).optional()
});

export const problemSchema = z.object({
  id: z.string(),
  description: z.string().min(1, 'Descrierea problemei este obligatorie'),
  severity: z.enum(['mild', 'moderate', 'severe', 'critical']).optional(),
  type: z.enum(['definitive', 'presumptive', 'differential']).optional(),
  resolved: z.boolean().optional(),
  icdCode: z.string().optional()
});

export const planSchema = z.object({
  investigations: z.string().optional(),
  treatments: z.string().optional(),
  homeCare: z.string().optional(),
  recallDate: z.string().optional(),
  recallReason: z.string().optional(),
  recallCreated: z.boolean().optional()
});

export const vaccinationSchema = z.object({
  product: z.string().min(1, 'Produsul vaccinului este obligatoriu'),
  dose: z.string().optional(),
  doseAmount: z.number().min(0).optional(),
  doseUnit: z.string().optional(),
  site: z.string().optional(),
  producer: z.string().optional(),
  lot: z.string().min(1, 'Seria/lot-ul este obligatorie pentru conformitate legală'),
  exp: z.string().min(1, 'Data de expirare este obligatorie pentru conformitate legală'),
  scheme: z.enum(['primar', 'rapel', 'booster']).optional(),
  nextDue: z.string().optional(),
  adverse: z.string().optional(),
  administeredAt: z.string().optional(),
  administeredBy: z.string().optional()
});

export const treatmentSchema = z.object({
  id: z.string(),
  drug: z.string().min(1, 'Medicamentul este obligatoriu'),
  drugId: z.string().optional(),
  dose: z.string().min(1, 'Doza este obligatorie'),
  doseAmount: z.number().min(0).optional(),
  doseUnit: z.string().optional(),
  route: z.enum(['oral', 'iv', 'im', 'sc', 'topical', 'other']),
  time: z.string().min(1, 'Ora este obligatorie'),
  qty: z.number().min(0.01, 'Cantitatea trebuie să fie > 0'),
  uom: z.string().min(1, 'Unitatea de măsură este obligatorie'),
  lot: z.string().optional(),
  exp: z.string().optional(),
  fromStock: z.boolean(),
  stockConsumed: z.boolean().optional()
}).refine(data => {
  // Dacă e din stoc, lot și exp sunt obligatorii
  if (data.fromStock && (!data.lot || !data.exp)) {
    return false;
  }
  return true;
}, {
  message: 'Pentru tratamente din stoc, seria/lot și expirarea sunt obligatorii'
});

export const prescriptionSchema = z.object({
  id: z.string(),
  drug: z.string().min(1),
  dosage: z.string().min(1),
  frequency: z.string().min(1),
  duration: z.string().min(1),
  instructions: z.string().optional(),
  qty: z.number().min(0.01),
  uom: z.string().min(1)
});

export const billingItemSchema = z.object({
  id: z.string(),
  itemId: z.string().optional(),
  name: z.string().min(1, 'Numele este obligatoriu'),
  qty: z.number().min(0.01, 'Cantitatea trebuie să fie > 0'),
  uom: z.string().min(1, 'UOM este obligatoriu'),
  unitPrice: z.number().min(0, 'Prețul unitar trebuie să fie >= 0'),
  total: z.number().min(0),
  lot: z.string().optional(),
  exp: z.string().optional(),
  fromStock: z.boolean(),
  taxRate: z.number().min(0).max(100).optional()
});

export const visitFormSchema = z.object({
  vitals: vitalsSchema,
  anamnesis: anamnesisSchema,
  exam: examSchema,
  problems: z.array(problemSchema),
  plan: planSchema,
  vaccination: vaccinationSchema.nullable(),
  treatments: z.array(treatmentSchema),
  prescriptions: z.array(prescriptionSchema),
  billingItems: z.array(billingItemSchema),
  notes: z.string().optional(),
  recommendations: z.string().optional(),
  ownerMessage: z.string().optional(),
  ownerMessageChannel: z.enum(['sms', 'email', 'whatsapp']),
  publicNote: z.boolean()
});

// Validare pentru semnare (cerințe minime)
export const visitSigningSchema = visitFormSchema.extend({
  exam: examSchema.refine(data => {
    // Trebuie să aibă fie "toate normale" fie note
    return data.allNormal === true || (data.notes && data.notes.trim().length > 0);
  }, {
    message: 'Examenul fizic trebuie completat (fie "toate normale", fie notele)'
  }),
  problems: z.array(problemSchema).min(1, 'Cel puțin un diagnostic/problemă este necesară')
}).refine(data => {
  // Dacă e vaccinare, vaccination trebuie completată
  if (data.vaccination) {
    return vaccinationSchema.safeParse(data.vaccination).success;
  }
  return true;
}, {
  message: 'Pentru vaccinare, toate câmpurile obligatorii trebuie completate'
});

export type VisitFormSchema = z.infer<typeof visitFormSchema>;
export type VisitSigningSchema = z.infer<typeof visitSigningSchema>;

